import axios, { AxiosRequestConfig } from 'axios';
import { Auth } from 'aws-amplify';
import { CacheAxiosResponse, CacheRequestConfig, setupCache } from 'axios-cache-interceptor';
import { Person } from 'model/person';
import { Project } from 'model/project';
import {
  CodeName, LengthGuidance, ProfileGeneration, ProfilePromptState, ProfilePromptVersion, ProfileReadiness,
} from 'model/profile-generator';
import { ProjectPerson, ProjectPersonInterval } from 'model/project-person';
import { ProjectTransferPayload, TransferPreview, TransferCommitResult } from 'model/project-transfer';
import { Client } from 'model/client';
import { Department } from 'model/department';
import { ProjectMemberPod } from 'model/project-member-pod';
import { ProjectRole, ProjectRoleInterval } from 'model/project-role';
import { ProjectRoleSkill } from 'model/project-role-skill';
import { ProjectExpense } from 'model/project-expense';
import { AuthContextUser } from 'contexts/auth-context-model';
import { Bookmark } from 'model/bookmark';
import { Settings } from 'model/settings';
import {
  FilteredResourcePermission, GlobalPermissions, PermissionGroup, PermissionsTypes, PredefinedFunctions, Role,
} from 'model/role-permissions';
import { Skill, SkillPerson } from 'model/skill';
import { Game, PersonGame } from 'model/game';
import { PersonExperience } from 'model/person-experience';
import { PersonHqProject, PersonProjectEntry } from 'model/person-project-history';
import {
  AI_CREDITS_CHANGED_EVENT, AiCreditBalance, AiCreditsOverview,
} from 'model/ai-credits';
import { FutureJoiner, isFutureJoinerId } from 'model/future-joiner';
import { isProfileCreationVisible } from 'utils/demo-mode';
import { LlmFeedbackEntry, LlmFeedbackReport, LlmRating } from 'model/llm-feedback';
import { Timeline } from 'model/timelines';
import { ChatMessage } from 'model/chatbot';
import { CheckpointReview, CheckpointTemplate } from 'model/checkpoint';
import { InvoiceRecord, InvoiceApprovalType } from 'model/invoice-record';
import { BillRate, Discount, RateInterval } from 'model/rates';
import { KwsStudio } from 'model/kws_studios';
import {
  DocumentRecord, DocumentType, DocumentQA, DocumentSearchResponse,
} from 'model/document';
import { Task } from 'model/task';
import { SkillContextProposal, SkillContextOverride } from 'model/skill-context';
import { CvUpload } from 'model/cv-upload';
import { InvoiceReportRow } from 'model/invoice-report';
import {
  loadProjects,
  loadProject,
  loadProjectPersons,
  loadPerson,
  loadPeople,
  serializePerson,
  serializeProjectPerson,
  serializeProject,
  serializeBoolean,
  serializeExpense,
  loadExpenses,
  loadExpense,
  loadProjectPerson,
  loadBookmarks,
  loadBookmark,
  serializeBookmark,
  loadPermission,
  loadSkillPersons,
  serializeSkillPerson,
  loadSkillPerson,
  loadGames,
  loadGame,
  loadPersonGames,
  serializePersonGame,
  loadPersonGame,
  loadFilteredResourcePermission,
  loadTimelineProjectPersonIntervals,
  loadRateIntervals,
  serializeRateInterval,
  loadRateInterval,
  serializeProjectRoleInterval,
  loadProjectRoleIntervals,
} from './api-serializer';
import { loadDate, serializeDate } from './date';
import { loadApiEndpoint } from './endpoints-config';
import { capturePageData } from './page-data-capture';
import { updateMaintenanceWindow } from './maintenance';

/*
 * An object representing cached IDs for different resource types
 */
export const CACHE_STORED_IDS: {
  projects: string[],
  people: string[],
  project_person: string[],
  departments: string[],
  clients: string[],
  bookmarks: string[],
  rateInterval: string[],
  kws_studios: string[],
} = {
  projects: [],
  people: [],
  project_person: [],
  departments: [],
  clients: [],
  bookmarks: [],
  rateInterval: [],
  kws_studios: [],
};

const apiInstance = axios.create({ baseURL: '' });
loadApiEndpoint().then((baseURL) => {
  apiInstance.defaults.baseURL = baseURL;
});

// Add response interceptor to extract maintenance headers
apiInstance.interceptors.response.use(
  (response) => {
    const startDate = response.headers['x-maintenance-start'] || null;
    const endDate = response.headers['x-maintenance-end'] || null;
    updateMaintenanceWindow(startDate, endDate);
    return response;
  },
  (error) => {
    // Also check headers on error responses
    if (error.response?.headers) {
      const startDate = error.response.headers['x-maintenance-start'] || null;
      const endDate = error.response.headers['x-maintenance-end'] || null;
      updateMaintenanceWindow(startDate, endDate);
    }
    return Promise.reject(error);
  },
);

const cachedApiInstance = axios.create({ baseURL: '' });
loadApiEndpoint().then((baseURL) => {
  cachedApiInstance.defaults.baseURL = baseURL;
});

// Add response interceptor for cached API instance as well
cachedApiInstance.interceptors.response.use(
  (response) => {
    const startDate = response.headers['x-maintenance-start'] || null;
    const endDate = response.headers['x-maintenance-end'] || null;
    updateMaintenanceWindow(startDate, endDate);
    return response;
  },
  (error) => {
    if (error.response?.headers) {
      const startDate = error.response.headers['x-maintenance-start'] || null;
      const endDate = error.response.headers['x-maintenance-end'] || null;
      updateMaintenanceWindow(startDate, endDate);
    }
    return Promise.reject(error);
  },
);

const cachedApi = setupCache(cachedApiInstance, {
  cacheTakeover: false,
  ttl: 1000 * 360, // 1 hour
});

/*
 * Given a resource type, removes all corresponding cached IDs from the cache storage
 */
const deleteCachesByType = async (type: string) => {
  if (!['projects', 'people', 'project_person', 'departments', 'clients', 'bookmarks', 'rate_interval', 'role_interval', 'kws_studios'].includes(type)) {
    return;
  }

  const cachedIds = CACHE_STORED_IDS[type as keyof typeof CACHE_STORED_IDS];

  if (cachedIds && cachedIds.length > 0) {
    await Promise.all(
      cachedIds.map(async (id) => {
        try {
          await cachedApi.storage.remove(id);
        } catch (error) {
          console.error(`Error removing cache for ${type} with ID ${id}:`, error);
        }
      }),
    );

    // Clear the array of cached IDs
    CACHE_STORED_IDS[type as keyof typeof CACHE_STORED_IDS] = [];
  }
};

/*
 * Given a resource type and an id pushes this id to the respective resource
 */
const addCacheIdByType = (type: string, id: string) => {
  if (!['projects', 'people', 'project_person', 'departments', 'clients', 'bookmarks', 'rate_interval', 'kws_studios'].includes(type)) {
    return;
  }

  CACHE_STORED_IDS[type as keyof typeof CACHE_STORED_IDS].push(id);
};

const getImpersonation = () => {
  const impersonationData = localStorage.getItem('impersonation');
  if (!impersonationData) return undefined;
  try {
    return JSON.parse(impersonationData);
  } catch (e) {
    localStorage.removeItem('impersonation');
    console.error('Error loading impersonation data', e);
  }
  return undefined;
};

// Cross-workspace user impersonation (prod1 → prod). Stored as a plain
// string (the target user's email) — backend gates the path to
// workspace=prod1 + SuperAdmin and silently falls through to normal
// auth if either gate fails.
const getImpersonatedUser = () => {
  const value = localStorage.getItem('impersonate_user');
  if (!value) return undefined;
  return value;
};

const authenticateRequest = async (requestConfig: AxiosRequestConfig) => {
  const currentSession = await Auth.currentSession();
  const accessToken = currentSession.getIdToken();
  const authenticatedRequestConfig = requestConfig;
  authenticatedRequestConfig.headers = {
    'Content-Type': 'application/json',
    'Access-Control-Allow-Origin': '*',
    Authorization: accessToken.getJwtToken(),
  };

  const impersonation = getImpersonation();
  if (impersonation) {
    authenticatedRequestConfig.headers['X-Impersonate-Roles'] = impersonation.join(',');
  }

  const impersonatedUser = getImpersonatedUser();
  if (impersonatedUser) {
    authenticatedRequestConfig.headers['X-Impersonate-User'] = impersonatedUser;
  }

  return authenticatedRequestConfig;
};

const makeAuthenticatedRequest = async <T = void>(requestConfig: AxiosRequestConfig) => {
  const authenticatedRequestConfig = await authenticateRequest(requestConfig);
  const response = await apiInstance<T>(authenticatedRequestConfig);
  // Local LM Studio assistant: no-op unless enabled for this user.
  capturePageData(requestConfig, response.data);
  return response.data;
};

const makeAuthenticatedCachedRequest = async <T = void>(requestConfig: CacheRequestConfig, resource_type: string) => {
  const authenticatedRequestConfig = await authenticateRequest(requestConfig);
  const response = await cachedApi<T>(authenticatedRequestConfig) as CacheAxiosResponse;

  // If we update, create or delete a resource we will empty all caches around this resource to maintain integrity with DB
  if (requestConfig.method !== 'GET') {
    if (response.status === 200) {
      await deleteCachesByType(resource_type);
    }
  } else if (response.id !== undefined && !response.cached) {
    addCacheIdByType(resource_type, response.id);
  }

  // Local LM Studio assistant: no-op unless enabled for this user.
  capturePageData(requestConfig, response.data);
  return response.data;
};

/* USER */

export const createUser = (user: AuthContextUser) => {
  const requestConfig: AxiosRequestConfig = {
    method: 'POST',
    url: '/user_create',
    data: user,
  };

  return makeAuthenticatedRequest<AuthContextUser>(requestConfig);
};

export const readUsers = async (userIds?: string[]): Promise<AuthContextUser[]> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/user_read',
  };
  if (userIds !== undefined) {
    const params = new URLSearchParams();
    userIds.forEach((userId: string) => params.append('email', userId));
    requestConfig.params = params;
  }

  const response = await makeAuthenticatedRequest<AuthContextUser[]>(requestConfig);
  return response;
};

// Source for the prod1-only "Impersonate User" picker. Returns prod's
// user list joined with prod-side names. Every prod user is
// impersonatable — the flow borrows their role bundle and overlays
// it on the caller's identity. Backend gates to workspace=prod1 +
// SuperAdmin.
export interface ImpersonatableUser {
  email: string;
  name: string;
  role_count: number;
}

export const readImpersonatableUsers = async (): Promise<ImpersonatableUser[]> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/impersonatable_users_read',
  };
  return makeAuthenticatedRequest<ImpersonatableUser[]>(requestConfig);
};

export const readUser = async (email: string): Promise<AuthContextUser | undefined> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: `/user_read?email=${encodeURIComponent(email)}`,
  };

  const response = await makeAuthenticatedRequest<AuthContextUser[]>(requestConfig);
  return response.length > 0 ? response[0] : undefined;
};

export const updateUser = (user: AuthContextUser) => {
  const requestConfig: AxiosRequestConfig = {
    method: 'PUT',
    url: '/user_update',
    data: user,
  };

  return makeAuthenticatedRequest<AuthContextUser>(requestConfig);
};

export const deleteUser = (email: string) => {
  const requestConfig: AxiosRequestConfig = {
    method: 'DELETE',
    url: '/user_delete',
    data: { email },
  };

  return makeAuthenticatedRequest(requestConfig);
};

/* PERSON */

export const createPerson = (person: Person) => {
  const requestConfig: AxiosRequestConfig = {
    method: 'POST',
    url: '/person_create',
    data: serializePerson(person),
  };

  return makeAuthenticatedCachedRequest(requestConfig, 'people');
};

export const invalidatePeopleCache = () => deleteCachesByType('people');

export const readPeople = async (personIds?: string[]): Promise<Person[]> => {
  let response: any[] = [];

  const requestConfig: CacheRequestConfig = {
    method: 'GET',
    url: '/person_read',
  };
  if (personIds !== undefined) {
    const params = new URLSearchParams();
    personIds.forEach((clientId: string) => params.append('id', clientId));
    requestConfig.params = params;

    response = await makeAuthenticatedRequest<Person[]>(requestConfig);
  } else {
    response = await makeAuthenticatedCachedRequest<Person[]>(requestConfig, 'people');
  }

  return loadPeople(response);
};

export const readPerson = async (personID: string): Promise<Person | undefined> => {
  const requestConfig: CacheRequestConfig = {
    method: 'GET',
    url: `/person_read?id=${personID}`,
  };

  const response = await makeAuthenticatedCachedRequest<Person[]>(requestConfig, 'people');
  return response.length > 0 ? loadPerson(response[0]) : undefined;
};

export const updatePerson = async (person: Person): Promise<Person> => {
  const requestConfig: CacheRequestConfig = {
    method: 'PUT',
    url: '/person_update',
    data: serializePerson(person),
  };

  // The lambda returns the saved record (with backend-injected fields
  // like `profile_updated_at` populated). Return it deserialised so
  // callers can refresh local state with the authoritative shape
  // rather than the optimistic one they sent.
  const saved = await makeAuthenticatedCachedRequest<any>(requestConfig, 'people');
  return loadPerson(saved);
};

// Profile Summary only — a partial update ({id, profile}) so it can never
// overwrite fields the person form is autosaving at the same time.
/* `aiAssist: 'cv_import'` once, on the save that brings in CV text: the server records who did it. */
export const updatePersonProfile = async (personId: string, profile: string, aiAssist?: 'cv_import'): Promise<Person> => {
  const requestConfig: CacheRequestConfig = {
    method: 'PUT',
    url: '/person_update',
    data: { id: personId, profile, ...(aiAssist ? { ai_assist: aiAssist } : {}) },
  };
  const saved = await makeAuthenticatedCachedRequest<any>(requestConfig, 'people');
  return loadPerson(saved);
};

export const deletePerson = (personId: string) => {
  const requestConfig: CacheRequestConfig = {
    method: 'DELETE',
    url: '/person_delete',
    data: { id: personId },
  };

  return makeAuthenticatedCachedRequest(requestConfig, 'people');
};

/* PROJECT */

export const createProject = async (project: Project): Promise<Project> => {
  const requestConfig: CacheRequestConfig = {
    method: 'POST',
    url: '/project_create',
    data: serializeProject(project),
  };

  const response = await makeAuthenticatedCachedRequest(requestConfig, 'projects');

  return loadProject(response);
};

export const readProject = async (projectID: string): Promise<Project | undefined> => {
  const requestConfig: CacheRequestConfig = {
    method: 'GET',
    url: `/project_read?id=${encodeURIComponent(projectID)}`,
  };

  const response = await makeAuthenticatedCachedRequest<any[]>(requestConfig, 'projects');

  return response.length > 0 ? loadProject(response[0]) : undefined;
};

export const readProjects = async (projectIds?: string[]): Promise<Project[]> => {
  let response: any[] = [];

  const requestConfig: CacheRequestConfig = {
    method: 'GET',
    url: '/project_read',
  };
  if (projectIds !== undefined) {
    const params = new URLSearchParams();
    projectIds.forEach((projectId: string) => params.append('id', projectId));
    requestConfig.params = params;
    response = await makeAuthenticatedRequest<any[]>(requestConfig);
  } else {
    response = await makeAuthenticatedCachedRequest<any[]>(requestConfig, 'projects');
  }

  return loadProjects(response);
};

// Force-refresh the project list, bypassing the 'projects' cache. Used by the Project Transfer
// form so a project created in another tab shows up without a full reload.
export const refreshProjects = async (): Promise<Project[]> => {
  await deleteCachesByType('projects');
  return readProjects();
};

/* PROJECT TRANSFER */
export const previewProjectTransfer = async (payload: ProjectTransferPayload): Promise<TransferPreview> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'POST',
    url: '/project_transfer_create',
    data: { ...payload, dry_run: true },
  };
  return makeAuthenticatedRequest<TransferPreview>(requestConfig);
};

export const commitProjectTransfer = async (payload: ProjectTransferPayload): Promise<TransferCommitResult> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'POST',
    url: '/project_transfer_create',
    data: { ...payload, dry_run: false },
  };
  return makeAuthenticatedRequest<TransferCommitResult>(requestConfig);
};

export const readPitches = async (pitchIds?: string[]): Promise<Project[]> => {
  const requestConfig: CacheRequestConfig = {
    method: 'GET',
    url: '/pitch_read',
  };
  if (pitchIds !== undefined) {
    const params = new URLSearchParams();
    pitchIds.forEach((id: string) => params.append('id', id));
    requestConfig.params = params;
  }
  const response = await makeAuthenticatedRequest<any[]>(requestConfig);
  return loadProjects(response);
};

export const updateProject = (project: Project): Promise<void> => {
  const requestConfig: CacheRequestConfig = {
    method: 'PUT',
    url: '/project_update',
    data: serializeProject(project),
  };

  return makeAuthenticatedCachedRequest(requestConfig, 'projects');
};

/**
 * Partial-update version of `updateProject`. Sends only `{id, ...patch}`
 * — the backend's `project_update` reads the existing record and only
 * applies fields present in `values`.
 *
 * Why this exists: full-record PUT (the regular `updateProject`) lets
 * concurrent autosave handlers stomp each other's work via stale
 * closures. Field A's save captures `project` snapshot, field B's save
 * captures a different `project` snapshot, whichever response lands
 * second overwrites the other field. Sending only the changed field
 * eliminates that class of race entirely.
 *
 * Use for autosave / single-field flows. The full-record `updateProject`
 * stays for create-then-redirect / commit-as-a-whole forms where the
 * caller is the source of truth on the entire record.
 */
export const patchProject = (
  id: string,
  patch: Partial<Project>,
  signal?: AbortSignal,
): Promise<void> => {
  const data: Record<string, unknown> = { id };
  // Date fields serialize specially — match what serializeProject does
  // so the backend sees consistent shapes regardless of which path was
  // used.
  Object.entries(patch).forEach(([k, v]) => {
    if (k === 'start_date' || k === 'end_date' || k === 'due_date' || k === 'likely_end_date') {
      data[k] = v == null ? null : serializeDate(v as Date);
    } else {
      data[k] = v;
    }
  });
  const requestConfig: CacheRequestConfig = {
    method: 'PUT',
    url: '/project_update',
    data,
    signal,
  };
  return makeAuthenticatedCachedRequest(requestConfig, 'projects');
};

export const deleteProject = (projectId: string) => {
  const requestConfig: CacheRequestConfig = {
    method: 'DELETE',
    url: '/project_delete',
    data: { id: projectId },
  };

  return makeAuthenticatedCachedRequest(requestConfig, 'projects');
};

/* PROJECT - PERSON */

// While profile creation is hidden (demo mode), future joiners' rows are left
// out of every read, as if the viewer lacked `future_joiner` (which is what
// the backend does for those viewers). Nothing they own then reaches state,
// so autosave can never touch them.
const withoutHiddenFutureJoiners = <T>(rows: T[] | null | undefined): T[] => (
  isProfileCreationVisible() ? (rows || []) : (rows || []).filter((row: any) => !isFutureJoinerId(row?.person_id))
);

export const readPersonProjects = async (personIds?: string[]): Promise<ProjectPerson[]> => {
  let response: any[] = [];

  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/project_person_read',
  };
  if (personIds !== undefined) {
    const params = new URLSearchParams();
    personIds.forEach((personId: string) => params.append('person_id', personId));
    requestConfig.params = params;

    response = await makeAuthenticatedRequest<any[]>(requestConfig);
  } else {
    response = await makeAuthenticatedCachedRequest<ProjectPerson[]>(requestConfig, 'project_person');
  }

  return loadProjectPersons(withoutHiddenFutureJoiners(response));
};

export const readProjectPersons = async (projectIds?: string[]): Promise<ProjectPerson[]> => {
  let response: any[] = [];

  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/project_person_read',
  };
  if (projectIds !== undefined) {
    const params = new URLSearchParams();
    projectIds.forEach((projectId: string) => params.append('project_id', projectId));
    requestConfig.params = params;

    response = await makeAuthenticatedRequest<any[]>(requestConfig);
  } else {
    response = await makeAuthenticatedCachedRequest<ProjectPerson[]>(requestConfig, 'project_person');
  }

  return loadProjectPersons(withoutHiddenFutureJoiners(response));
};

export const createProjectPerson = async (projectPerson: ProjectPerson) => {
  const requestConfig: AxiosRequestConfig = {
    method: 'POST',
    url: '/project_person_create',
    data: serializeProjectPerson(projectPerson),
  };

  const response = await makeAuthenticatedCachedRequest<ProjectPerson>(requestConfig, 'project_person');
  return loadProjectPerson(response);
};

export const updateProjectPerson = async (projectPerson: ProjectPerson) => {
  const requestConfig: AxiosRequestConfig = {
    method: 'PUT',
    url: '/project_person_update',
    data: serializeProjectPerson(projectPerson),
  };

  const response = await makeAuthenticatedCachedRequest<ProjectPerson>(requestConfig, 'project_person');
  return loadProjectPerson(response);
};

export const deleteProjectPerson = (projectId: string, sortKey: string) => {
  const requestConfig: AxiosRequestConfig = {
    method: 'DELETE',
    url: '/project_person_delete',
    data: { project_id: projectId, sort_key: sortKey },
  };

  return makeAuthenticatedCachedRequest(requestConfig, 'project_person');
};

/* SKILLS */

export const createSkill = async (skill: Skill): Promise<Skill> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'POST',
    url: '/skills_create',
    data: skill,
  };

  return makeAuthenticatedRequest(requestConfig);
};

export const readSkills = async (): Promise<Skill[]> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/skills_read',
  };

  return makeAuthenticatedRequest(requestConfig);
};

export const updateSkill = (skill: Skill) => {
  const requestConfig: CacheRequestConfig = {
    method: 'PUT',
    url: '/skills_update',
    data: skill,
  };

  return makeAuthenticatedRequest(requestConfig);
};

export const deleteSkills = (skillIds: string[]) => {
  const requestConfig: AxiosRequestConfig = {
    method: 'DELETE',
    url: '/skills_delete',
    data: { id: skillIds },
  };

  return makeAuthenticatedRequest(requestConfig);
};

export const mergeSkills = (skillIds: string[], mergedSkillName: string) => {
  const requestConfig: AxiosRequestConfig = {
    method: 'POST',
    url: '/skills_merge',
    data: {
      skill_ids: skillIds,
      merged_skill_name: mergedSkillName,
    },
  };

  return makeAuthenticatedRequest(requestConfig);
};

export const getSuggestedSkills = (personId: string): Promise<Skill[]> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: `/get_suggested_skills?person_id=${encodeURIComponent(personId)}`,
  };

  return makeAuthenticatedRequest(requestConfig);
};

export const getPopularDepartmentSkills = (personId: string): Promise<Skill[]> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: `/get_popular_department_skills?person_id=${encodeURIComponent(personId)}`,
  };

  return makeAuthenticatedRequest(requestConfig);
};

export const createSuggestedDiscardedSkill = async (discardedSkill: string, personId: string): Promise<Skill> => {
  const discardedSuggestedSkill = {
    discarded_skills: [discardedSkill],
    person_id: personId,
  };

  const requestConfig: AxiosRequestConfig = {
    method: 'POST',
    url: '/discarded_suggested_skills_create',
    data: discardedSuggestedSkill,
  };

  return makeAuthenticatedRequest(requestConfig);
};

/* SKILL - PERSON */

export const readSkillPersons = async (personIds?: string[], skillIds?: string[]): Promise<SkillPerson[]> => {
  let response: any[] = [];

  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/skill_person_read',
  };

  if (personIds !== undefined || skillIds !== undefined) {
    const params = new URLSearchParams();
    if (personIds !== undefined) {
      personIds.forEach((personId: string) => params.append('person_id', personId));
    }
    if (skillIds !== undefined) {
      skillIds.forEach((skillId: string) => params.append('skill_id', skillId));
    }
    requestConfig.params = params;

    response = await makeAuthenticatedRequest<any[]>(requestConfig);
  } else {
    response = await makeAuthenticatedCachedRequest<SkillPerson[]>(requestConfig, 'skill_person');
  }

  return loadSkillPersons(response);
};

export const createSkillPerson = async (skillPerson: SkillPerson) => {
  const requestConfig: AxiosRequestConfig = {
    method: 'POST',
    url: '/skill_person_create',
    data: serializeSkillPerson(skillPerson),
  };

  const response = await makeAuthenticatedCachedRequest<SkillPerson>(requestConfig, 'skill_person');
  return loadSkillPerson(response);
};

export const updateSkillPerson = async (skillPerson: Partial<SkillPerson>) => {
  const requestConfig: AxiosRequestConfig = {
    method: 'PUT',
    url: '/skill_person_update',
    data: serializeSkillPerson(skillPerson),
  };

  const response = await makeAuthenticatedCachedRequest<SkillPerson>(requestConfig, 'skill_person');
  return loadSkillPerson(response);
};

export const deleteSkillPerson = (personId: string, sortKey: string) => {
  const requestConfig: AxiosRequestConfig = {
    method: 'DELETE',
    url: '/skill_person_delete',
    data: { person_id: personId, sort_key: sortKey },
  };

  return makeAuthenticatedCachedRequest(requestConfig, 'skill_person');
};

/* GAME */

export const readGames = async (): Promise<Game[]> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/game_read',
  };

  const response = await makeAuthenticatedCachedRequest<Game[]>(requestConfig, 'game');
  return loadGames(response);
};

export const searchGames = async (searchTerm: string, limit?: number): Promise<Game[]> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/game_search',
    params: {
      search_term: searchTerm,
      ...(limit && { limit }),
    },
  };

  const response = await makeAuthenticatedRequest<Game[]>(requestConfig);
  return loadGames(response);
};

export const importGameAndCreatePersonGame = async (
  gameData: Partial<Game>,
  personGame: Partial<PersonGame>,
) => {
  const requestConfig: AxiosRequestConfig = {
    method: 'POST',
    url: '/game_import_and_create_person_game',
    data: {
      game: gameData,
      person_game: serializePersonGame(personGame),
    },
  };

  const response = await makeAuthenticatedCachedRequest<{
    game: Game;
    person_game: PersonGame;
  }>(requestConfig, 'person_game');

  return {
    game: loadGame(response.game),
    person_game: loadPersonGame(response.person_game),
  };
};

/* PERSON - GAME */

export const readPersonGames = async (personIds?: string[], gameIds?: string[]): Promise<PersonGame[]> => {
  let response: any[] = [];

  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/person_game_read',
  };

  if (personIds !== undefined || gameIds !== undefined) {
    const params = new URLSearchParams();
    if (personIds !== undefined) {
      personIds.forEach((personId: string) => params.append('person_id', personId));
    }
    if (gameIds !== undefined) {
      gameIds.forEach((gameId: string) => params.append('game_id', gameId));
    }
    requestConfig.params = params;

    response = await makeAuthenticatedRequest<any[]>(requestConfig);
  } else {
    response = await makeAuthenticatedCachedRequest<PersonGame[]>(requestConfig, 'person_game');
  }

  return loadPersonGames(response);
};

export const createPersonGame = async (personGame: PersonGame) => {
  const requestConfig: AxiosRequestConfig = {
    method: 'POST',
    url: '/person_game_create',
    data: serializePersonGame(personGame),
  };

  const response = await makeAuthenticatedCachedRequest<PersonGame>(requestConfig, 'person_game');
  return loadPersonGame(response);
};

export const updatePersonGame = async (personGame: Partial<PersonGame>) => {
  const requestConfig: AxiosRequestConfig = {
    method: 'PUT',
    url: '/person_game_update',
    data: serializePersonGame(personGame),
  };

  const response = await makeAuthenticatedCachedRequest<PersonGame>(requestConfig, 'person_game');
  return loadPersonGame(response);
};

export const deletePersonGame = (personId: string, sortKey: string) => {
  const requestConfig: AxiosRequestConfig = {
    method: 'DELETE',
    url: '/person_game_delete',
    data: { person_id: personId, sort_key: sortKey },
  };

  return makeAuthenticatedCachedRequest(requestConfig, 'person_game');
};

/* PERSON - EXPERIENCE */

export const readPersonExperience = async (personId: string): Promise<PersonExperience[]> => {
  const params = new URLSearchParams();
  params.append('person_id', personId);
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: `/person_experience_read?${params.toString()}`,
  };
  const response = await makeAuthenticatedRequest<PersonExperience[]>(requestConfig);
  return response ?? [];
};

export const createPersonExperience = async (
  entry: Omit<PersonExperience, 'sort_key'>,
  signal?: AbortSignal,
): Promise<PersonExperience> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'POST',
    url: '/person_experience_create',
    data: entry,
    signal,
  };
  return makeAuthenticatedRequest<PersonExperience>(requestConfig) as Promise<PersonExperience>;
};

export const updatePersonExperience = async (
  entry: PersonExperience,
  signal?: AbortSignal,
): Promise<PersonExperience> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'PUT',
    url: '/person_experience_update',
    data: entry,
    signal,
  };
  return makeAuthenticatedRequest<PersonExperience>(requestConfig) as Promise<PersonExperience>;
};

export const deletePersonExperience = (personId: string, sortKey: string) => {
  const requestConfig: AxiosRequestConfig = {
    method: 'DELETE',
    url: '/person_experience_delete',
    data: { person_id: personId, sort_key: sortKey },
  };
  return makeAuthenticatedRequest(requestConfig);
};

/* PERSON - PROJECT HISTORY (person page → Profile → Projects) */

export const readPersonHqProjects = async (personId: string): Promise<PersonHqProject[]> => {
  const params = new URLSearchParams();
  params.append('person_id', personId);
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: `/person_hq_projects_read?${params.toString()}`,
  };
  const response = await makeAuthenticatedRequest<PersonHqProject[]>(requestConfig);
  return response ?? [];
};

export const readPersonProjectHistory = async (personId: string): Promise<PersonProjectEntry[]> => {
  const params = new URLSearchParams();
  params.append('person_id', personId);
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: `/person_project_history_read?${params.toString()}`,
  };
  const response = await makeAuthenticatedRequest<PersonProjectEntry[]>(requestConfig);
  return response ?? [];
};

// Upsert — the client owns the sort_key (hq#<project_id> / other#<uuid>).
export const savePersonProjectEntry = async (
  entry: PersonProjectEntry,
  signal?: AbortSignal,
): Promise<PersonProjectEntry> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'PUT',
    url: '/person_project_history_update',
    data: entry,
    signal,
  };
  return makeAuthenticatedRequest<PersonProjectEntry>(requestConfig) as Promise<PersonProjectEntry>;
};

export const deletePersonProjectEntry = (personId: string, sortKey: string) => {
  const requestConfig: AxiosRequestConfig = {
    method: 'DELETE',
    url: '/person_project_history_delete',
    data: { person_id: personId, sort_key: sortKey },
  };
  return makeAuthenticatedRequest(requestConfig);
};

/* FUTURE JOINERS (crud/future_joiner.py) */

export const readFutureJoiners = async (): Promise<FutureJoiner[]> => {
  if (!isProfileCreationVisible()) return [];
  const requestConfig: AxiosRequestConfig = { method: 'GET', url: '/future_joiner_read' };
  return (await makeAuthenticatedRequest<FutureJoiner[]>(requestConfig)) ?? [];
};

export const readFutureJoiner = async (id: string): Promise<FutureJoiner | undefined> => {
  const requestConfig: AxiosRequestConfig = { method: 'GET', url: `/future_joiner_read?id=${encodeURIComponent(id)}` };
  const rows = await makeAuthenticatedRequest<FutureJoiner[]>(requestConfig);
  return rows?.[0];
};

export const createFutureJoiner = async (values: Partial<FutureJoiner>): Promise<FutureJoiner> => {
  const requestConfig: AxiosRequestConfig = { method: 'POST', url: '/future_joiner_create', data: values };
  return makeAuthenticatedRequest<FutureJoiner>(requestConfig) as Promise<FutureJoiner>;
};

// Partial update: send only the fields that changed (plus id).
export const updateFutureJoiner = async (values: Partial<FutureJoiner> & { id: string }): Promise<FutureJoiner> => {
  const requestConfig: AxiosRequestConfig = { method: 'PUT', url: '/future_joiner_update', data: values };
  return makeAuthenticatedRequest<FutureJoiner>(requestConfig) as Promise<FutureJoiner>;
};

// Deletes the future joiner AND everything they own (profile sets, CVs, entries).
export const deleteFutureJoiner = (id: string) => {
  const requestConfig: AxiosRequestConfig = { method: 'DELETE', url: '/future_joiner_delete', data: { id } };
  return makeAuthenticatedRequest(requestConfig);
};

export interface FutureJoinerLinkResult {
  id: string;
  person_id: string;
  moved: { profiles: number; project_entries: number; cv_uploads: number; skills: number; summary: boolean };
}

// Hand everything over to their HQ person (once HiBob has created them), then delete the record.
export const linkFutureJoiner = async (id: string, personId: string): Promise<FutureJoinerLinkResult> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'POST', url: '/future_joiner_link_create', data: { id, person_id: personId },
  };
  return makeAuthenticatedRequest<FutureJoinerLinkResult>(requestConfig) as Promise<FutureJoinerLinkResult>;
};

/* LLM FEEDBACK (crud/llm_feedback.py) */

export const saveLlmFeedback = async (values: {
  target_id: string;
  feature?: string;
  rating?: LlmRating | null;
  tags?: string[];
  comment?: string;
  // Snapshot of what was rated, for features whose output isn't stored server-side.
  context?: Record<string, unknown>;
}): Promise<LlmFeedbackEntry> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'POST',
    url: '/llm_feedback_create',
    data: { feature: 'profile_generation', ...values },
  };
  return makeAuthenticatedRequest<LlmFeedbackEntry>(requestConfig) as Promise<LlmFeedbackEntry>;
};

// Your own feedback on these generations (anyone who can generate).
export const readMyLlmFeedback = async (targetIds: string[], feature = 'profile_generation'): Promise<LlmFeedbackEntry[]> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/llm_feedback_read',
    params: new URLSearchParams([['target_id', targetIds.join(',')], ['feature', feature]]),
  };
  return (await makeAuthenticatedRequest<LlmFeedbackEntry[]>(requestConfig)) ?? [];
};

// Everything, enriched — reviewers (`llm_feedback`) only.
export const readLlmFeedbackReport = async (): Promise<LlmFeedbackReport> => {
  const requestConfig: AxiosRequestConfig = { method: 'GET', url: '/llm_feedback_read' };
  return makeAuthenticatedRequest<LlmFeedbackReport>(requestConfig) as Promise<LlmFeedbackReport>;
};

export const updateLlmFeedback = async (values: { id: string; status?: string; reviewer_note?: string }): Promise<LlmFeedbackEntry> => {
  const requestConfig: AxiosRequestConfig = { method: 'PUT', url: '/llm_feedback_update', data: values };
  return makeAuthenticatedRequest<LlmFeedbackEntry>(requestConfig) as Promise<LlmFeedbackEntry>;
};

/* PROJECT WRITE-UP DRAFTS (person page → Profile → Projects) */

export type ProjectWriteupMode = 'skills' | 'checkpoint_project' | 'checkpoint_period';

/* A skill a checkpoint draft names that the person hasn't got yet. */
export interface WriteupSkillSuggestion {
  skill_id: string;
  name: string; // HQ's name for it
  mentioned_as: string; // as the draft wrote it
  proficiency: number; // suggested from the reviews, 1-3
}

/* A skill a checkpoint draft names that HQ's skills list has no entry for: added as a custom skill. */
export interface WriteupCustomSkill {
  name: string;
  proficiency: number; // suggested from the reviews, 1-3
}

export interface ProjectWriteupDraft {
  text: string;
  mode: ProjectWriteupMode;
  sources: { skills?: number; reviews?: number; review_dates?: string[]; review_ids?: string[] };
  draft_id: string; // for rating the draft (drafts aren't stored)
  model?: string;
  word_count?: number;
  /* "Make 50% longer" steps taken so far on this draft (0 = a fresh draft). */
  lengthen_step?: number;
  max_lengthen_steps?: number;
  can_lengthen?: boolean;
  /* Only when requested (suggest_skills) on a checkpoint draft. */
  skill_suggestions?: WriteupSkillSuggestion[];
  unmatched_skills?: WriteupCustomSkill[];
}

/* AI CREDITS (crud/ai_credits.py) */

// Every AI call spends a credit (or is refused when none are left): tell the
// credit badges to refresh either way.
const spendingAiCredit = async <T>(request: Promise<T>): Promise<T> => {
  try {
    return await request;
  } finally {
    window.dispatchEvent(new Event(AI_CREDITS_CHANGED_EVENT));
  }
};

export const readAiCredits = async (): Promise<AiCreditBalance> => {
  const requestConfig: AxiosRequestConfig = { method: 'GET', url: '/ai_credits_read' };
  return makeAuthenticatedRequest<AiCreditBalance>(requestConfig) as Promise<AiCreditBalance>;
};

export const readAiCreditsForUser = async (user: string): Promise<AiCreditBalance> => {
  const requestConfig: AxiosRequestConfig = { method: 'GET', url: `/ai_credits_read?user=${encodeURIComponent(user)}` };
  return makeAuthenticatedRequest<AiCreditBalance>(requestConfig) as Promise<AiCreditBalance>;
};

export const readAiCreditsOverview = async (): Promise<AiCreditsOverview> => {
  const requestConfig: AxiosRequestConfig = { method: 'GET', url: '/ai_credits_read?scope=all' };
  return makeAuthenticatedRequest<AiCreditsOverview>(requestConfig) as Promise<AiCreditsOverview>;
};

// Admin: +25 credits for 28 days (no refresh). Reason required (audit).
// Admin: extra credits for 28 days (amount defaults to 25 server-side). Reason required.
export const grantAiCredits = async (user: string, reason: string, amount?: number) => {
  const requestConfig: AxiosRequestConfig = { method: 'POST', url: '/ai_credits_create', data: { user, reason, amount } };
  return makeAuthenticatedRequest(requestConfig);
};

// Admin: monthly allowance for a role or one user; null clears it. Reason required.
export const setAiCreditAllowance = async (values: {
  target_type: 'user' | 'role'; target: string; monthly_allowance: number | null; reason: string;
}) => {
  const requestConfig: AxiosRequestConfig = { method: 'PUT', url: '/ai_credits_update', data: values };
  return makeAuthenticatedRequest(requestConfig);
};

// A draft only — nothing is stored server-side (crud/project_writeup.py).
export const createProjectWriteup = async (values: {
  person_id: string;
  project_id: string;
  mode: ProjectWriteupMode;
  skill_ids?: string[];
  /* Lengthening: the current draft, and which step (1-6) this is. */
  lengthen_from?: string;
  lengthen_step?: number;
  /* Checkpoint drafts: also list skills it names that aren't on their list. */
  suggest_skills?: boolean;
}): Promise<ProjectWriteupDraft> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'POST',
    url: '/project_writeup_create',
    data: values,
  };
  return spendingAiCredit(makeAuthenticatedRequest<ProjectWriteupDraft>(requestConfig) as Promise<ProjectWriteupDraft>);
};

/* REVIEWS */
export const readReviews = async (): Promise<any[]> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/checkpoint_reviews',
  };

  return makeAuthenticatedRequest(requestConfig);
};

export const readReviewsAdmin = async (): Promise<any[]> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/checkpoint_reviews_admin',
  };

  return makeAuthenticatedRequest(requestConfig);
};

export const readReview = async (id: string, view_all: string): Promise<string[]> => {
  let baseUrl = '/checkpoint_details_read?review_id=';
  if (view_all === 'true') {
    baseUrl = '/checkpoint_details_read?view_all=true&review_id=';
  }
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: baseUrl + id,
  };
  return makeAuthenticatedRequest(requestConfig);
};

export const updateReview = async (review: Partial<CheckpointReview>): Promise<string[]> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'PUT',
    url: '/checkpoint_details_update',
    data: review,
  };

  return makeAuthenticatedRequest(requestConfig);
};

export const scheduleReview = async (
  scheduler_id: string,
  reviewer_id: string,
  reviewee_id: string,
  review_start: string,
  template_id: string,
): Promise<string[]> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'POST',
    url: '/checkpoint_details_create',
    data: {
      scheduler_id, reviewer_id, reviewee_id, review_start, template_id,
    },
  };

  return makeAuthenticatedRequest(requestConfig);
};

export const getMyReports = async (review_id: string): Promise<{ [key: string]: any }> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: `/checkpoint_get_person_reports?review_id=${review_id}`,
  };

  return makeAuthenticatedRequest(requestConfig);
};

export const readActiveTemplates = async (): Promise<CheckpointTemplate[]> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/checkpoint_templates_active',
  };

  return makeAuthenticatedRequest(requestConfig);
};

export const checkpointAdminUnlockReview = async (review_id: string): Promise<string[]> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: `/checkpoint_admin_unlock_review?review_id=${review_id}`,
  };

  return makeAuthenticatedRequest(requestConfig);
};

export const checkpointGetFilters = async (): Promise<any[]> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/checkpoint_get_filters',
  };

  return makeAuthenticatedRequest(requestConfig);
};

export const checkpointSwapParticipants = async (person_id: string, review_id: string): Promise<any[]> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: `/checkpoint_swap_participants?person_id=${person_id}&review_id=${review_id}`,
  };

  return makeAuthenticatedRequest(requestConfig);
};

/* CLIENT */

export const createClient = (client: Client) => {
  const requestConfig: AxiosRequestConfig = {
    method: 'POST',
    url: '/client_create',
    data: client,
  };

  return makeAuthenticatedCachedRequest(requestConfig, 'clients');
};

export const readClients = async (clientIds?: string[]): Promise<Client[]> => {
  let response: any[] = [];

  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/client_read',
  };
  if (clientIds !== undefined) {
    const params = new URLSearchParams();
    clientIds.forEach((clientId: string) => params.append('id', clientId));
    requestConfig.params = params;

    response = await makeAuthenticatedRequest<Client[]>(requestConfig);
  } else {
    response = await makeAuthenticatedCachedRequest<Client[]>(requestConfig, 'clients');
  }

  return response;
};

export const readClient = async (clientId: string): Promise<Client | undefined> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: `/client_read?id=${clientId}`,
  };

  const response = await makeAuthenticatedCachedRequest<Client[]>(requestConfig, 'clients');
  return response.length > 0 ? response[0] : undefined;
};

export const updateClient = (client: Client) => {
  const requestConfig: AxiosRequestConfig = {
    method: 'PUT',
    url: '/client_update',
    data: client,
  };

  return makeAuthenticatedCachedRequest(requestConfig, 'clients');
};

export const deleteClient = (clientId: string) => {
  const requestConfig: AxiosRequestConfig = {
    method: 'DELETE',
    url: '/client_delete',
    data: { id: clientId },
  };

  return makeAuthenticatedCachedRequest(requestConfig, 'clients');
};

/* DEPARTMENTS */

export const createDepartment = (department: Department) => {
  const requestConfig: AxiosRequestConfig = {
    method: 'POST',
    url: '/department_create',
    data: department,
  };

  return makeAuthenticatedCachedRequest(requestConfig, 'departments');
};

export const readDepartments = async (departmentIds?: string[]): Promise<Department[]> => {
  let response: any[] = [];

  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/department_read',
  };
  if (departmentIds !== undefined) {
    const params = new URLSearchParams();
    departmentIds.forEach((departmentId: string) => params.append('id', departmentId));
    requestConfig.params = params;
    response = await makeAuthenticatedRequest<Department[]>(requestConfig);
  } else {
    response = await makeAuthenticatedCachedRequest<Department[]>(requestConfig, 'departments');
  }

  return response;
};

export const readDepartment = async (departmentId: string): Promise<Department | undefined> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: `/department_read?id=${departmentId}`,
  };

  const response = await makeAuthenticatedCachedRequest<Department[]>(requestConfig, 'departments');
  return response.length > 0 ? response[0] : undefined;
};

export const updateDepartment = (department: Department) => {
  const requestConfig: AxiosRequestConfig = {
    method: 'PUT',
    url: '/department_update',
    data: department,
  };

  return makeAuthenticatedCachedRequest(requestConfig, 'departments');
};

export const deleteDepartment = (departmentId: string) => {
  const requestConfig: AxiosRequestConfig = {
    method: 'DELETE',
    url: '/department_delete',
    data: { id: departmentId },
  };

  return makeAuthenticatedCachedRequest(requestConfig, 'departments');
};

/* Roles */

export const createProjectRole = async (role: ProjectRole): Promise<ProjectRole> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'POST',
    url: '/project_member_role_create',
    data: role,
  };

  const response = await makeAuthenticatedRequest<ProjectRole>(requestConfig);
  return response;
};

export const readProjectRolesByPod = async (podId?: string): Promise<ProjectRole[]> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/project_member_role_read',
  };
  if (podId !== undefined) {
    const params = new URLSearchParams();
    params.append('project_member_pod_id', podId);
    requestConfig.params = params;
  }

  return makeAuthenticatedRequest<ProjectRole[]>(requestConfig);
};

export const updateProjectRole = (role: ProjectRole) => {
  const requestConfig: AxiosRequestConfig = {
    method: 'PUT',
    url: '/project_member_role_update',
    data: role,
  };

  return makeAuthenticatedRequest(requestConfig);
};

export const deleteProjectRole = (roleId: string, podId: string) => {
  const requestConfig: AxiosRequestConfig = {
    method: 'DELETE',
    url: '/project_member_role_delete',
    data: { id: roleId, project_member_pod_id: podId },
  };

  return makeAuthenticatedRequest(requestConfig);
};

/* PODS */

export const createProjectMemberPod = async (pod: ProjectMemberPod): Promise<ProjectMemberPod> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'POST',
    url: '/project_member_pod_create',
    data: pod,
  };

  const response = await makeAuthenticatedRequest<ProjectMemberPod>(requestConfig);
  return response;
};

export const readProjectMemberPodsByProject = async (projectId?: string): Promise<ProjectMemberPod[]> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/project_member_pod_read',
  };
  if (projectId !== undefined) {
    const params = new URLSearchParams();
    params.append('project_id', projectId);
    requestConfig.params = params;
  }

  return makeAuthenticatedRequest<ProjectMemberPod[]>(requestConfig);
};

export const updateProjectMemberPod = (pod: ProjectMemberPod) => {
  const requestConfig: AxiosRequestConfig = {
    method: 'PUT',
    url: '/project_member_pod_update',
    data: pod,
  };

  return makeAuthenticatedRequest(requestConfig);
};

export const deleteProjectMemberPod = (podId: string, projectId: string) => {
  const requestConfig: AxiosRequestConfig = {
    method: 'DELETE',
    url: '/project_member_pod_delete',
    data: { id: podId, project_id: projectId },
  };

  return makeAuthenticatedRequest(requestConfig);
};

/* EXPENSES */

export const createProjectExpense = async (expense: ProjectExpense): Promise<ProjectExpense> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'POST',
    url: '/project_expense_create',
    data: serializeExpense(expense),
  };

  const response = await makeAuthenticatedRequest<ProjectExpense>(requestConfig);
  return loadExpense(response);
};

export const readProjectExpenses = async (projectId?: string): Promise<ProjectExpense[]> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/project_expense_read',
  };
  if (projectId !== undefined) {
    const params = new URLSearchParams();
    params.append('project_id', projectId);
    requestConfig.params = params;
  }

  const response = await makeAuthenticatedRequest<ProjectExpense[]>(requestConfig);
  return loadExpenses(response);
};

export const updateProjectExpense = async (expense: ProjectExpense) => {
  const requestConfig: AxiosRequestConfig = {
    method: 'PUT',
    url: '/project_expense_update',
    data: serializeExpense(expense),
  };

  const response = await makeAuthenticatedRequest<ProjectExpense>(requestConfig);
  return loadExpense(response);
};

export const deleteProjectExpense = (expenseId: string, projectId: string) => {
  const requestConfig: AxiosRequestConfig = {
    method: 'DELETE',
    url: '/project_expense_delete',
    data: { id: expenseId, project_id: projectId },
  };

  return makeAuthenticatedRequest(requestConfig);
};

/* BOOKMARK */

export const createBookmark = (bookmark: Bookmark) => {
  const requestConfig: CacheRequestConfig = {
    method: 'POST',
    url: '/bookmark_create',
    data: serializeBookmark(bookmark),
  };

  return makeAuthenticatedCachedRequest(requestConfig, 'bookmarks');
};

export const readBookmarks = async (bookmarkIds?: string[]): Promise<Bookmark[]> => {
  const requestConfig: CacheRequestConfig = {
    method: 'GET',
    url: '/bookmark_read',
  };
  if (bookmarkIds !== undefined) {
    const params = new URLSearchParams();
    bookmarkIds.forEach((bookmarkId: string) => params.append('id', bookmarkId));
    requestConfig.params = params;
  }

  const response = await makeAuthenticatedCachedRequest<Bookmark[]>(requestConfig, 'bookmarks');
  return loadBookmarks(response);
};

export const readBookmark = async (bookmarkID: string): Promise<Bookmark | undefined> => {
  const requestConfig: CacheRequestConfig = {
    method: 'GET',
    url: '/bookmark_read',
  };
  const params = new URLSearchParams();

  params.append('id', bookmarkID);

  requestConfig.params = params;

  const response = await makeAuthenticatedCachedRequest<Bookmark[]>(requestConfig, 'bookmarks');
  return response.length > 0 ? loadBookmark(response[0]) : undefined;
};

export const readBookmarkByShortLink = async (shortLink: string): Promise<Bookmark | undefined> => {
  const requestConfig: CacheRequestConfig = {
    method: 'GET',
    url: '/bookmark_read',
  };
  const params = new URLSearchParams();

  params.append('short_link', shortLink);

  requestConfig.params = params;

  const response = await makeAuthenticatedCachedRequest<Bookmark[]>(requestConfig, 'bookmarks');
  return response.length > 0 ? loadBookmark(response[0]) : undefined;
};

export const updateBookmark = (bookmark: Bookmark) => {
  const requestConfig: CacheRequestConfig = {
    method: 'PUT',
    url: '/bookmark_update',
    data: bookmark,
  };

  return makeAuthenticatedCachedRequest(requestConfig, 'bookmarks');
};

export const deleteBookmark = (personId: string, bookmarkId: string) => {
  const requestConfig: CacheRequestConfig = {
    method: 'DELETE',
    url: '/bookmark_delete',
    data: { id: bookmarkId, person_id: personId },
  };

  return makeAuthenticatedCachedRequest(requestConfig, 'bookmarks');
};

/* SETTINGS */

export const createSettings = (settings: Settings) => {
  const requestConfig: AxiosRequestConfig = {
    method: 'POST',
    url: '/settings_create',
    data: settings,
  };

  return makeAuthenticatedRequest(requestConfig);
};

export const readSettings = async (): Promise<Settings | undefined> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/settings_read',
  };

  const response = await makeAuthenticatedRequest<Settings[]>(requestConfig);
  return response[0];
};

export const updateSettings = (settings: Settings) => {
  const requestConfig: AxiosRequestConfig = {
    method: 'PUT',
    url: '/settings_update',
    data: settings,
  };

  return makeAuthenticatedRequest(requestConfig);
};

/* PROJECT ROLE ASSIGNMENTS */

export interface ProjectRoleAssignmentRow {
  project_id: string;
  pod_name: string;
  role_name: string;
  person_id: string;
  person_name: string;
  utilisation: number | null;
  project_role_interval_sort_key: string;
}

export const readProjectRoleAssignments = (
  params?: { project_id?: string; as_of?: string },
): Promise<ProjectRoleAssignmentRow[]> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/project_role_assignments_read',
    params: params || {},
  };
  return makeAuthenticatedRequest(requestConfig);
};

/* INVOICES */

export const generateInvoice = (
  projectId: string,
  startDate: Date,
  endDate: Date,
  draft: boolean,
  customText: string,
  customId: string,
  purchase_order_number: string,
  milestone_name: string,
  invoiceDate?: string,
  useRoleName?: boolean,
): Promise<string> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/generate_invoice',
  };
  const params = new URLSearchParams();
  params.append('project_id', projectId);
  params.append('start_date', serializeDate(startDate) as string);
  params.append('end_date', serializeDate(endDate) as string);
  params.append('is_draft', serializeBoolean(draft));
  params.append('custom_text', customText);
  params.append('custom_id', customId);
  params.append('purchase_order_number', purchase_order_number);
  params.append('milestone_name', milestone_name);
  if (invoiceDate) {
    params.append('invoice_date', invoiceDate);
  }
  if (useRoleName) {
    params.append('use_role_name', 'true');
  }
  requestConfig.params = params;

  return makeAuthenticatedRequest(requestConfig);
};

/* INVOICE RECORDS */

export const readInvoiceRecords = (projectId: string, billingMonth?: string): Promise<InvoiceRecord[]> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/invoice_record_read',
    params: { project_id: projectId, ...(billingMonth ? { billing_month: billingMonth } : {}) },
  };
  return makeAuthenticatedRequest(requestConfig);
};

export const approveInvoiceRecord = (projectId: string, sortKey: string, approvalType: InvoiceApprovalType): Promise<InvoiceRecord> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'POST',
    url: '/invoice_record_approve',
    data: { project_id: projectId, sort_key: sortKey, approval_type: approvalType },
  };
  return makeAuthenticatedRequest(requestConfig);
};

export const unapproveInvoiceRecord = (projectId: string, sortKey: string, approvalType: InvoiceApprovalType): Promise<InvoiceRecord> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'POST',
    url: '/invoice_record_unapprove',
    data: { project_id: projectId, sort_key: sortKey, approval_type: approvalType },
  };
  return makeAuthenticatedRequest(requestConfig);
};

export const getInvoiceRecordUrl = (projectId: string, sortKey: string): Promise<{ url: string }> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/invoice_record_get_url',
    params: { project_id: projectId, sort_key: sortKey },
  };
  return makeAuthenticatedRequest(requestConfig);
};

/* PERMISSIONS */

export const getPermissions = (): Promise<GlobalPermissions | undefined> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/permissions_read',
  };

  return makeAuthenticatedRequest(requestConfig);
};

export const getCustomPermissionsFunctions = (): Promise<PredefinedFunctions[] | undefined> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/read_custom_permissions_functions',
  };

  return makeAuthenticatedRequest(requestConfig);
};

export const checkPermissionsInFunction = async (function_name: string): Promise<PermissionsTypes | undefined> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/check_function_permissions',
  };

  const params = new URLSearchParams();
  params.append('function_name', function_name);
  requestConfig.params = params;

  const response = await makeAuthenticatedRequest<string>(requestConfig);

  return loadPermission(response[0]);
};

// Raw result of one permission function (no parsing) — the per-name
// fallback when the batch endpoint isn't available.
export const checkFunctionPermissionRaw = async (function_name: string): Promise<unknown> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/check_function_permissions',
    params: new URLSearchParams([['function_name', function_name]]),
  };
  return makeAuthenticatedRequest<unknown>(requestConfig);
};

export interface FunctionCheckResult {
  status: number;
  result: unknown;
}

// All of a user's permission functions in one request; each succeeds or
// fails on its own (crud/permission.py::check_function_permissions_batch).
export const checkFunctionPermissionsBatch = async (functionNames: string[]): Promise<Record<string, FunctionCheckResult>> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/check_function_permissions_batch',
    params: new URLSearchParams([['function_names', functionNames.join(',')]]),
  };
  return makeAuthenticatedRequest<Record<string, FunctionCheckResult>>(requestConfig) as Promise<Record<string, FunctionCheckResult>>;
};

export const checkFilterPermissionsInFunction = async (function_name: string): Promise<FilteredResourcePermission[] | undefined> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/check_function_permissions',
  };

  const params = new URLSearchParams();
  params.append('function_name', function_name);
  requestConfig.params = params;

  const response = await makeAuthenticatedRequest<string>(requestConfig);

  return loadFilteredResourcePermission(response);
};

export const updatePermissions = (data: Role): Promise<GlobalPermissions | undefined> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'PUT',
    url: '/permissions_update',
    data,
  };

  return makeAuthenticatedRequest(requestConfig);
};

export const createPermissions = (data: Role): Promise<GlobalPermissions | undefined> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'POST',
    url: '/permissions_create',
    data,
  };

  return makeAuthenticatedRequest(requestConfig);
};

export interface BulkRoleResult {
  role_id: string;
  added?: number;
  removed?: number;
  already_had?: number;
  did_not_have?: number;
  total: number;
  written: number;
  failed_emails?: string[];
}

export const applyRoleToAllUsers = (roleId: string): Promise<BulkRoleResult> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'POST',
    url: '/role_apply_to_all_users',
    data: { role_id: roleId },
  };
  return makeAuthenticatedRequest(requestConfig);
};

export const removeRoleFromAllUsers = (roleId: string): Promise<BulkRoleResult> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'POST',
    url: '/role_remove_from_all_users',
    data: { role_id: roleId },
  };
  return makeAuthenticatedRequest(requestConfig);
};

export const getPermissionGroups = (): Promise<PermissionGroup[] | undefined> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/permissions_group_read',
  };
  return makeAuthenticatedRequest(requestConfig);
};

export const createPermissionGroup = (group: PermissionGroup): Promise<PermissionGroup | undefined> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'POST',
    url: '/permissions_group_create',
    data: group,
  };
  return makeAuthenticatedRequest(requestConfig);
};

export const updatePermissionGroup = (group: PermissionGroup): Promise<PermissionGroup | undefined> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'PUT',
    url: '/permissions_group_update',
    data: group,
  };
  return makeAuthenticatedRequest(requestConfig);
};

export const deletePermissionGroup = (groupId: string): Promise<any> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'DELETE',
    url: '/permissions_group_delete',
    data: { id: groupId },
  };
  return makeAuthenticatedRequest(requestConfig);
};

/* TIMELINES */

export const getProjectPersonIntervalsFromTimelines = async (projectIds?: string[], personIds?: string[]): Promise<ProjectPersonInterval[]> => {
  let response: any[] = [];

  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/project_person_timeline_read',
  };
  if (projectIds !== undefined) {
    const params = new URLSearchParams();
    projectIds.forEach((projectId: string) => params.append('project_id', projectId));
    requestConfig.params = params;
  }
  if (personIds !== undefined) {
    const params = new URLSearchParams();
    personIds.forEach((personId: string) => params.append('person_id', personId));
    requestConfig.params = params;
  }

  response = await makeAuthenticatedRequest<Timeline[]>(requestConfig);
  return withoutHiddenFutureJoiners(loadTimelineProjectPersonIntervals(response));
};

/* RATE INTERVALS */

export const readRateIntervals = async (projectIds?: string[]): Promise<[BillRate[], Discount[]]> => {
  let response: any[] = [];

  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/rate_interval_read',
  };
  if (projectIds !== undefined) {
    const params = new URLSearchParams();
    projectIds.forEach((projectId: string) => params.append('project_id', projectId));
    requestConfig.params = params;

    response = await makeAuthenticatedRequest<any[]>(requestConfig);
  } else {
    response = await makeAuthenticatedCachedRequest<RateInterval[]>(requestConfig, 'rate_interval');
  }

  return loadRateIntervals(response);
};

export const createRateInterval = async (rateInterval: BillRate | Discount): Promise<BillRate | Discount | RateInterval> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'POST',
    url: '/rate_interval_create',
    data: serializeRateInterval(rateInterval),
  };

  const response = await makeAuthenticatedCachedRequest<RateInterval>(requestConfig, 'rate_interval');
  return loadRateInterval(response);
};

export const updateRateInterval = async (rateInterval: BillRate | Discount): Promise<BillRate | Discount | RateInterval> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'PUT',
    url: '/rate_interval_update',
    data: serializeRateInterval(rateInterval),
  };

  const response = await makeAuthenticatedCachedRequest<RateInterval>(requestConfig, 'rate_interval');
  return loadRateInterval(response);
};

export const deleteRateIntervals = (projectId: string, entityId: string, sortKey: string) => {
  const requestConfig: AxiosRequestConfig = {
    method: 'DELETE',
    url: '/rate_interval_delete',
    data: { entity_id: entityId, sort_key: sortKey, project_id: projectId },
  };

  return makeAuthenticatedCachedRequest(requestConfig, 'rate_interval');
};

/* ROLE INTERVALS */
export const readProjectRoleInterval = async (projectIds?: string[]): Promise<ProjectRoleInterval[]> => {
  let response: any[] = [];

  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/project_role_interval_read',
  };
  if (projectIds !== undefined) {
    const params = new URLSearchParams();
    projectIds.forEach((projectId: string) => params.append('project_id', projectId));
    requestConfig.params = params;

    response = await makeAuthenticatedRequest<any[]>(requestConfig);
  } else {
    response = await makeAuthenticatedCachedRequest<ProjectRoleInterval[]>(requestConfig, 'project_person');
  }

  return loadProjectRoleIntervals(response);
};

export const createProjectRoleInterval = async (projectRoleInterval: ProjectRoleInterval) => {
  const requestConfig: AxiosRequestConfig = {
    method: 'POST',
    url: '/project_role_interval_create',
    data: serializeProjectRoleInterval(projectRoleInterval),
  };

  const response = await makeAuthenticatedCachedRequest<ProjectPerson>(requestConfig, 'role_interval');
  return loadProjectPerson(response);
};

export const updateProjectRoleInterval = async (projectRoleInterval: ProjectRoleInterval) => {
  const requestConfig: AxiosRequestConfig = {
    method: 'PUT',
    url: '/project_role_interval_update',
    data: serializeProjectRoleInterval(projectRoleInterval),
  };

  const response = await makeAuthenticatedCachedRequest<ProjectPerson>(requestConfig, 'role_interval');
  return loadProjectPerson(response);
};

export const deleteProjectRoleInterval = (sortKey: string, projectId: string) => {
  const requestConfig: AxiosRequestConfig = {
    method: 'DELETE',
    url: '/project_role_interval_delete',
    data: { project_id: projectId, sort_key: sortKey },
  };

  return makeAuthenticatedCachedRequest(requestConfig, 'role_interval');
};

/* PROJECT ROLE SKILLS */
export const readProjectRoleSkills = async (projectRoleId: string): Promise<ProjectRoleSkill[]> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: `/project_role_skill_read?project_role_id=${encodeURIComponent(projectRoleId)}`,
  };
  return makeAuthenticatedRequest(requestConfig);
};

export const createProjectRoleSkill = async (skill: Omit<ProjectRoleSkill, 'sort_key'>): Promise<ProjectRoleSkill> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'POST',
    url: '/project_role_skill_create',
    data: skill,
  };
  return makeAuthenticatedRequest(requestConfig);
};

export const deleteProjectRoleSkill = async (projectRoleId: string, sortKey: string): Promise<void> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'DELETE',
    url: '/project_role_skill_delete',
    data: { project_role_id: projectRoleId, sort_key: sortKey },
  };
  return makeAuthenticatedRequest(requestConfig);
};

/* PEOPLE MAP GET LOCATIONS */
export const PeopleMapLocations = async (): Promise<any[]> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/people_map_locations',
  };

  return makeAuthenticatedRequest(requestConfig);
};

export const PeopleMapOptInOut = async (new_state: string): Promise<any[]> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: `/people_map_opt_in_out?new_state=${new_state}`,
  };

  return makeAuthenticatedRequest(requestConfig);
};

/* COMO */

export const ComoJoin = async (): Promise<any[]> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/como_join',
  };

  return makeAuthenticatedRequest(requestConfig);
};

export const ComoUserStatus = async (): Promise<any[]> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/como_user_status',
  };

  return makeAuthenticatedRequest(requestConfig);
};

/* CHATBOT */

export const ChatbotGetChatHistory = async (): Promise<ChatMessage[]> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/chatbot_get_chat_history',
  };

  return makeAuthenticatedRequest<ChatMessage[]>(requestConfig);
};

export const ChatbotSendMessage = async (message: ChatMessage): Promise<any> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'POST',
    url: '/chatbot_message_create',
    data: {
      message: message.content,
    },
  };

  return makeAuthenticatedRequest<any>(requestConfig);
};

export const ChatbotGetNewMessages = async (): Promise<ChatMessage[]> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/chatbot_message_read',
  };
  return makeAuthenticatedRequest<ChatMessage[]>(requestConfig);
};

/* INTERNAL FUNCTIONS */

export const InternalReadSmarttimeBob = async (): Promise<any[]> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/internal_smarttime_bob_read',
  };

  return makeAuthenticatedRequest(requestConfig);
};

export const InternalReadAllocationData = async (year: string, month: string): Promise<any[]> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/internal_unallocated_people_read',
  };
  const params = new URLSearchParams();
  params.append('year', year);
  params.append('month', month);
  requestConfig.params = params;

  return makeAuthenticatedRequest(requestConfig);
};

export const HolidaysReadMyProjectData = async (start_date: string, end_date: string): Promise<any[]> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/holidays_my_projects_read',
  };
  const params = new URLSearchParams();
  params.append('start_date', start_date);
  params.append('end_date', end_date);
  requestConfig.params = params;

  return makeAuthenticatedRequest(requestConfig);
};

export const HolidaysReadAllProjectData = async (start_date: string, end_date: string): Promise<any[]> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/holidays_all_projects_read',
  };
  const params = new URLSearchParams();
  params.append('start_date', start_date);
  params.append('end_date', end_date);
  requestConfig.params = params;

  return makeAuthenticatedRequest(requestConfig);
};

// KWS Studios
export const getKwsStudios = (): Promise<KwsStudio[]> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/kws_studios_read',
  };

  return makeAuthenticatedCachedRequest(requestConfig, 'kws_studios');
};

export const profileRead = async (personId: string, profileId?: string, includeDeleted?: boolean, projectId?: string): Promise<any[]> => {
  const params = new URLSearchParams();
  if (personId) params.append('person_id', personId);
  if (profileId) params.append('profile_id', profileId);
  if (projectId) params.append('project_id', projectId);
  if (includeDeleted) params.append('include_deleted', 'true');
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/profile_read',
    params,
  };
  return withoutHiddenFutureJoiners(await makeAuthenticatedRequest<any[]>(requestConfig));
};

export const profileCreate = async (profile: any): Promise<any> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'POST',
    url: '/profile_create',
    data: profile,
  };
  return makeAuthenticatedRequest(requestConfig);
};

export const profileUpdate = async (profile: any): Promise<any> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'PUT',
    url: '/profile_update',
    data: profile,
  };
  return makeAuthenticatedRequest(requestConfig);
};

export const profileDelete = async (profileId: string): Promise<any> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'DELETE',
    url: '/profile_delete',
    data: { profile_id: profileId },
  };
  return makeAuthenticatedRequest(requestConfig);
};

export const profileUndelete = async (profile: any): Promise<any> => profileUpdate({ ...profile, deleted_at: null });

// Client-profile generator — gem (system prompt) versions + generations.
export const readProfilePrompt = async (): Promise<ProfilePromptState> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/profile_prompt_read',
  };
  return makeAuthenticatedRequest(requestConfig);
};

/** Saves prompt + code names + length guidance together as a new config version. */
export const createProfilePromptVersion = async (version: {
  promptText: string, note?: string, codeNames: CodeName[], lengthGuidance: LengthGuidance, maxSoftographyTitles: number,
}): Promise<ProfilePromptVersion> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'POST',
    url: '/profile_prompt_create',
    data: {
      prompt_text: version.promptText,
      note: version.note,
      code_names: version.codeNames,
      length_guidance: version.lengthGuidance,
      max_softography_titles: version.maxSoftographyTitles,
    },
  };
  return makeAuthenticatedRequest(requestConfig);
};

/**
 * Starts an async generation; poll with `readProfileGenerations`. Pass
 * `feedback` + `baseGenerationId` to revise an earlier draft instead.
 */
/**
 * Start a generation. With `baseGenerationId`, revise that draft by `feedback`,
 * a new length (`targetWords`) or both; `targetWords` alone on a fresh run sets
 * its length.
 */
export const createProfileGeneration = async (
  profileId: string,
  options?: { feedback?: string, baseGenerationId?: string, targetWords?: number },
): Promise<ProfileGeneration> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'POST',
    url: '/profile_generation_create',
    data: {
      profile_id: profileId,
      ...(options?.feedback ? { feedback: options.feedback } : {}),
      ...(options?.baseGenerationId ? { base_generation_id: options.baseGenerationId } : {}),
      ...(options?.targetWords ? { target_words: options.targetWords } : {}),
    },
  };
  return spendingAiCredit(makeAuthenticatedRequest(requestConfig));
};

/** Records that a generation was inserted into its profile (quality tracking). */
export const markProfileGenerationInserted = async (generationId: string): Promise<ProfileGeneration> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'PUT',
    url: '/profile_generation_update',
    data: { id: generationId, inserted: true },
  };
  return makeAuthenticatedRequest(requestConfig);
};

/** Thin-data warnings + the facts the generator will use, before generating. */
export const readProfileReadiness = async (profileId: string): Promise<ProfileReadiness> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/profile_readiness_read',
    params: { profile_id: profileId },
  };
  return makeAuthenticatedRequest(requestConfig);
};

/** Newest first. Pass a generation id, or a profile id for its history. */
export const readProfileGenerations = async (filter: { id?: string, profileId?: string }): Promise<ProfileGeneration[]> => {
  const params = new URLSearchParams();
  if (filter.id) params.append('id', filter.id);
  if (filter.profileId) params.append('profile_id', filter.profileId);
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/profile_generation_read',
    params,
  };
  return makeAuthenticatedRequest(requestConfig);
};

// Pitch user likelihood — per-user scenario overrides
export interface PitchUserLikelihoodRecord {
  person_id: string;
  pitch_id: string;
  likelihood: number;
}

export const readPitchUserLikelihoods = async (): Promise<PitchUserLikelihoodRecord[]> => {
  const requestConfig: AxiosRequestConfig = { method: 'GET', url: '/pitch_user_likelihood_read' };
  return makeAuthenticatedRequest(requestConfig);
};

export const savePitchUserLikelihood = async (pitchId: string, likelihood: number): Promise<PitchUserLikelihoodRecord> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'POST',
    url: '/pitch_user_likelihood_create',
    data: { pitch_id: pitchId, likelihood },
  };
  return makeAuthenticatedRequest(requestConfig);
};

export const deletePitchUserLikelihood = async (pitchId: string): Promise<void> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'DELETE',
    url: '/pitch_user_likelihood_delete',
    data: { pitch_id: pitchId },
  };
  return makeAuthenticatedRequest(requestConfig);
};

/* TASKS */

export const readTasks = async (): Promise<Task[]> => {
  const requestConfig: AxiosRequestConfig = { method: 'GET', url: '/tasks_read' };
  return makeAuthenticatedRequest(requestConfig);
};

export const completeTask = async (taskId: string, outcome?: string): Promise<Task> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'POST',
    url: '/tasks_complete',
    data: { id: taskId, ...(outcome ? { outcome } : {}) },
  };
  return makeAuthenticatedRequest(requestConfig);
};

/* SKILL CONTEXT PROPOSALS + OVERRIDES */

export const createSkillContextProposal = async (
  skillId: string,
  proposedMeaning: string,
  currentMeaningAtProposalTime: string,
): Promise<SkillContextProposal> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'POST',
    url: '/skill_context_proposal_create',
    data: {
      skill_id: skillId,
      proposed_meaning: proposedMeaning,
      current_meaning_at_proposal_time: currentMeaningAtProposalTime,
    },
  };
  return makeAuthenticatedRequest(requestConfig);
};

export const readSkillContextProposal = async (proposalId: string): Promise<SkillContextProposal | undefined> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: `/skill_context_proposal_read?id=${encodeURIComponent(proposalId)}`,
  };
  const rows = await makeAuthenticatedRequest<SkillContextProposal[]>(requestConfig);
  return rows && rows.length > 0 ? rows[0] : undefined;
};

export const readSkillContextProposalsForSkill = async (skillId: string): Promise<SkillContextProposal[]> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: `/skill_context_proposal_read?skill_id=${encodeURIComponent(skillId)}`,
  };
  return makeAuthenticatedRequest(requestConfig);
};

export const resolveSkillContextProposal = async (
  proposalId: string,
  decision: 'approve' | 'reject',
  reason?: string,
): Promise<SkillContextProposal> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'POST',
    url: '/skill_context_proposal_resolve',
    data: { id: proposalId, decision, ...(reason ? { reason } : {}) },
  };
  return makeAuthenticatedRequest(requestConfig);
};

export const readSkillContextOverrides = async (): Promise<SkillContextOverride[]> => {
  const requestConfig: AxiosRequestConfig = { method: 'GET', url: '/skill_context_override_read' };
  return makeAuthenticatedRequest(requestConfig);
};

export const deleteSkillContextOverride = async (skillId: string): Promise<void> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'DELETE',
    url: '/skill_context_override_delete',
    data: { skill_id: skillId },
  };
  return makeAuthenticatedRequest(requestConfig);
};

/* CV UPLOADS */

const fileToBase64 = (file: File): Promise<string> => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const result = reader.result as string;
    // FileReader returns "data:<mime>;base64,<payload>"; strip the prefix.
    const base64 = result.includes(',') ? result.split(',', 2)[1] : result;
    resolve(base64);
  };
  reader.onerror = () => reject(reader.error);
  reader.readAsDataURL(file);
});

export const uploadCv = async (personId: string, file: File): Promise<CvUpload> => {
  const fileContent = await fileToBase64(file);
  const requestConfig: AxiosRequestConfig = {
    method: 'POST',
    url: '/cv_upload_create',
    data: {
      person_id: personId,
      file_name: file.name,
      content_type: file.type || 'application/octet-stream',
      file_content: fileContent,
    },
  };
  return spendingAiCredit(makeAuthenticatedRequest(requestConfig));
};

export const readCvUploadsForPerson = async (personId: string): Promise<CvUpload[]> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: `/cv_upload_read?person_id=${encodeURIComponent(personId)}`,
  };
  return makeAuthenticatedRequest(requestConfig);
};

export const deleteCvUpload = async (uploadId: string): Promise<void> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'DELETE',
    url: '/cv_upload_delete',
    data: { id: uploadId },
  };
  return makeAuthenticatedRequest(requestConfig);
};

export const readInvoiceReport = (startDate: string, endDate: string): Promise<InvoiceReportRow[]> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: '/invoice_report_read',
    params: { start_date: startDate, end_date: endDate },
  };
  return makeAuthenticatedRequest(requestConfig);
};

/* DOCUMENTS */

export const readDocuments = async (filters?: {
  id?: string;
  type?: 'project_info' | 'infosec';
  project_id?: string;
  client_id?: string;
}): Promise<DocumentRecord[]> => {
  const params = new URLSearchParams();
  if (filters?.id) params.append('id', filters.id);
  if (filters?.type) params.append('type', filters.type);
  if (filters?.project_id) params.append('project_id', filters.project_id);
  if (filters?.client_id) params.append('client_id', filters.client_id);
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: `/document_read${params.toString() ? `?${params}` : ''}`,
  };
  return makeAuthenticatedRequest(requestConfig);
};

export const updateDocument = async (
  patch: Partial<DocumentRecord> & { id: string; type: DocumentType },
): Promise<DocumentRecord> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'PUT',
    url: '/document_update',
    data: patch,
  };
  return makeAuthenticatedRequest(requestConfig);
};

export const deleteDocument = async (id: string): Promise<void> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'DELETE',
    url: '/document_delete',
    data: { id },
  };
  return makeAuthenticatedRequest(requestConfig);
};

export const searchDocuments = async (
  query: string,
  filters?: { type?: 'project_info' | 'infosec'; project_id?: string; client_id?: string },
): Promise<DocumentSearchResponse> => {
  const params = new URLSearchParams({ query });
  if (filters?.type) params.append('type', filters.type);
  if (filters?.project_id) params.append('project_id', filters.project_id);
  if (filters?.client_id) params.append('client_id', filters.client_id);
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: `/document_search?${params}`,
  };
  return makeAuthenticatedRequest(requestConfig);
};

export const readDocumentQAs = async (documentId: string): Promise<DocumentQA[]> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: `/document_qa_read?document_id=${encodeURIComponent(documentId)}`,
  };
  return makeAuthenticatedRequest(requestConfig);
};

export const createDocumentQA = async (qa: Omit<DocumentQA, 'qa_id'> & { qa_id?: string }): Promise<DocumentQA> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'POST',
    url: '/document_qa_create',
    data: qa,
  };
  return makeAuthenticatedRequest(requestConfig);
};

export const updateDocumentQA = async (qa: DocumentQA): Promise<DocumentQA> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'PUT',
    url: '/document_qa_update',
    data: qa,
  };
  return makeAuthenticatedRequest(requestConfig);
};

export const deleteDocumentQA = async (documentId: string, qaId: string): Promise<void> => {
  const requestConfig: AxiosRequestConfig = {
    method: 'DELETE',
    url: '/document_qa_delete',
    data: { document_id: documentId, qa_id: qaId },
  };
  return makeAuthenticatedRequest(requestConfig);
};

export interface CurrencyDiscount {
  amount: number;
  description: string;
}

export interface PersonAssignment {
  project_id: string;
  project_name: string;
  is_bench: boolean;
  utilisation: number;
  // Discount breakdown — present only when the caller has rate_interval Read.
  billed_utilisation?: number; // assigned x (1 - %off)
  discount_pct?: number; // combined percentage off (0..100)
  currency_discounts?: CurrencyDiscount[]; // fixed-£ discounts (can't be a %)
  discount_end_date?: string; // earliest end of the discounts applying now (YYYY-MM-DD)
  discount_open_ended?: boolean; // a discount applying now has NO end date — it never ends
  // Expected renewal: past the project's hard end, still counted only because
  // of its soft likely_end_date. They free up if the renewal doesn't happen.
  held_by_likely_end?: boolean;
  nominal_end_date?: string | null; // project's hard end (YYYY-MM-DD)
  likely_end_date?: string | null; // held until (YYYY-MM-DD)
  likely_end_reason?: string | null;
}

export interface AvailablePerson extends Person {
  utilisation_total: number;
  bench_utilisation_total: number;
  spare_capacity: number;
  assignments: PersonAssignment[];
  // Present only when the caller has rate_interval Read.
  billed_utilisation_total?: number;
  has_discount?: boolean;
  // Most recent day they generated revenue + on which project (rate_interval
  // Read only). null when they've never billed.
  last_billed_date?: Date | null;
  last_paying_project?: string | null;
  // Present only when some of their load is a likely-end hold (see
  // PersonAssignment.held_by_likely_end). Such people are listed even with
  // spare_capacity 0, so planners don't miss them.
  held_by_likely_end?: boolean;
  likely_end_held_utilisation?: number;
  spare_capacity_if_not_renewed?: number;
}

export const readAvailablePeople = async (asOf?: string): Promise<AvailablePerson[]> => {
  const params = new URLSearchParams();
  if (asOf) params.set('as_of', asOf);
  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: params.toString() ? `/available_people_read?${params}` : '/available_people_read',
  };
  const response = await makeAuthenticatedRequest<any[]>(requestConfig);
  return response.map((row) => ({
    ...loadPerson(row),
    utilisation_total: row.utilisation_total ?? 0,
    bench_utilisation_total: row.bench_utilisation_total ?? 0,
    spare_capacity: row.spare_capacity ?? 0,
    assignments: row.assignments ?? [],
    billed_utilisation_total: row.billed_utilisation_total,
    has_discount: row.has_discount,
    last_billed_date: loadDate(row.last_billed_date),
    last_paying_project: row.last_paying_project,
  }));
};
